/**
 * App version and build information utilities
 */

export interface AppVersionInfo {
  version: string;
  name: string;
  buildTime: string;
  isProduction: boolean;
}

/**
 * Get the current app version from environment variables
 */
export const getAppVersion = (): string =>
  process.env.REACT_APP_VERSION || 'unknown';

/**
 * Get the app name from environment variables
 */
export const getAppName = (): string => process.env.REACT_APP_NAME || 'webapp';

/**
 * Get the build timestamp
 */
export const getBuildTime = (): string =>
  process.env.REACT_APP_BUILD_TIME || new Date().toISOString();

/**
 * Check if running in production environment
 */
export const isProduction = (): boolean =>
  process.env.NODE_ENV === 'production';

/**
 * Get comprehensive app version information
 */
export const getAppVersionInfo = (): AppVersionInfo => ({
  version: getAppVersion(),
  name: getAppName(),
  buildTime: getBuildTime(),
  isProduction: isProduction()
});

/**
 * Get a formatted version string for display
 */
export const getVersionDisplay = (): string => {
  const info = getAppVersionInfo();
  const env = info.isProduction ? 'prod' : 'dev';
  return `${info.name} v${info.version} (${env})`;
};

/**
 * Log version information to console (useful for debugging)
 */
export const logVersionInfo = (): void => {
  const info = getAppVersionInfo();
  /* eslint-disable no-console */
  console.group('🚀 App Version Info ');
  console.log('Name:', info.name);
  console.log('Version:', info.version);
  console.log('Build Time:', info.buildTime);
  console.log('Environment:', info.isProduction ? 'Production' : 'Development');
  console.groupEnd();
  /* eslint-enable no-console */
};
